import { useMutation, useQueryClient } from "@tanstack/react-query";
import { z } from "zod";

import { SHIPMENT_TOAST_MESSAGES } from "~/features/shipment-management/constants/toast-messages.ts";
import { useShipmentNotifications } from "~/features/shipment-management/hooks/use-shipment-notifications.ts";
import type { ZipCode } from "~/shared/schemas/location-state-schema.ts";
import type { ShipmentCheckCall } from "~/shared/schemas/shipment-management-schema.ts";
import { apiRoutes } from "~/shared/services/api/api-routes.ts";
import { patchAndValidate } from "~/shared/services/api/fetch-client.ts";

const putTrackHistory = async (trackHistoryData: {
	trackHistory: ShipmentCheckCall;
	poNumber: string;
	message: string;
	selectedZipCode: ZipCode | null;
}) => {
	const url = apiRoutes.PUTDELETETRACKHISTORY.replace(
		":poNumber",
		trackHistoryData.poNumber,
	).replace(
		":trackHistoryId",
		trackHistoryData.trackHistory.id?.toString() ?? "",
	);
	const resp = await patchAndValidate(
		url,
		{
			remark: trackHistoryData.message,
			city:
				trackHistoryData.selectedZipCode?.city.name ||
				trackHistoryData.trackHistory.city,
			state:
				trackHistoryData.selectedZipCode?.city?.state?.abbreviation ||
				trackHistoryData.trackHistory.state,
			postalCode:
				trackHistoryData.selectedZipCode?.postalCode ||
				trackHistoryData.trackHistory.zip,
			country:
				trackHistoryData.selectedZipCode?.city?.state?.country?.abbreviation ||
				trackHistoryData.trackHistory.country,
		},
		z.unknown(),
	);
	if (resp.success) {
		return resp.data;
	}

	throw new Error(resp.error);
};

export const usePutTrackHistory = (options?: { onSuccess?: () => void }) => {
	const queryClient = useQueryClient();
	const { showSuccess, showError } = useShipmentNotifications();

	const { mutate, mutateAsync, isPending, error, data, reset } = useMutation({
		mutationFn: (trackHistory: {
			trackHistory: ShipmentCheckCall;
			poNumber: string;
			message: string;
			selectedZipCode: ZipCode | null;
		}) => putTrackHistory(trackHistory),
		onSuccess: async () => {
			await queryClient.invalidateQueries({ queryKey: ["shipment"] });
			showSuccess(SHIPMENT_TOAST_MESSAGES.checkCallUpdated);
			options?.onSuccess?.();
		},
		onError: () => {
			showError("Failed to update tracking history. Please try again.");
		},
	});

	return {
		putTrackHistory: mutate,
		putTrackHistoryAsync: mutateAsync,
		isPuttingTrackHistory: isPending,
		error,
		data,
		resetError: reset,
	};
};
